/**
 * Sprint BG-MasterIO — 共通 JSON Import/Export helpers (M1-M10 全 master 共通)。
 *
 * 旧 `old/apps/admin/src/features/cardPrint/configIO.ts` の Blob + URL.createObjectURL pattern と
 * 型ガード validator pattern を踏襲。
 *
 * 設計方針:
 *   - pure helper のみ (DOM / Firestore 直接依存なし、テスト容易)
 *   - schema は `{ exportedAt, schemaVersion, items }` の envelope 形式 (1 件 export も同 envelope に統一)
 *   - validator は呼び出し側が提供する type guard (`(raw) => T | null`) を取り、不正 item は warning に集約して skip
 *   - file download / file read は別 helper (downloadJsonFile / readJsonFile) で副作用を分離
 */

/** Export envelope schema (現行 v1)。 */
export const MASTER_IO_SCHEMA_VERSION = '1' as const;

/**
 * Sprint BG-MasterIO follow-up — 1 ファイルあたりの import 上限件数。
 * 数千件の悪意ファイルで Firestore write を暴走させない緩い safeguard。
 * 業務上の master 1 種類が 500 件を超えるケースは現状無いため十分。
 */
export const MAX_IMPORT_ITEMS = 500;

/**
 * Sprint BG-MasterIO follow-up — import 時にシステム field を除去 (Firestore Timestamp 上書き防止)。
 *
 * 背景: export JSON は createdAt/updatedAt 等を ISO 文字列で含むが、再 import 時に
 * そのまま upsertMaster の merge:true に渡すと既存 Firestore Timestamp を文字列で
 * 上書きしてしまい、後続の orderBy('updatedAt') 等が壊れる。
 *
 * 除去対象:
 *   - createdAt / updatedAt / createdBy / updatedBy (システム監査)
 *   - archivedAt (アーカイブ状態は import で復活させない)
 *   - schemaVersion / version (master 側で再生成 / 管理)
 */
export function stripSystemFields<T extends object>(
  item: T,
): Omit<
  T,
  'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'archivedAt' | 'schemaVersion' | 'version'
> {
  // 構造化 destructure は T の field 制約が緩いため、object spread + delete で実装
  // (T が strict な interface でも index signature を要求しない)
  const clone: Record<string, unknown> = { ...(item as Record<string, unknown>) };
  delete clone.createdAt;
  delete clone.updatedAt;
  delete clone.createdBy;
  delete clone.updatedBy;
  delete clone.archivedAt;
  delete clone.schemaVersion;
  delete clone.version;
  return clone as unknown as Omit<
    T,
    | 'createdAt'
    | 'updatedAt'
    | 'createdBy'
    | 'updatedBy'
    | 'archivedAt'
    | 'schemaVersion'
    | 'version'
  >;
}

export interface MasterIoEnvelope<T> {
  /** ISO8601 文字列、export 時刻 (再 import 時の参考情報)。 */
  exportedAt: string;
  /** envelope schema バージョン。将来 v2 で破壊変更時に切替。 */
  schemaVersion: string;
  /** master items (1 件 export も配列で統一)。 */
  items: T[];
}

export interface ParseResult<T> {
  items: T[];
  /** validator が `null` を返した index/理由を行ベースで保持。`Item #3: missing displayName` 等。 */
  errors: string[];
}

/**
 * 任意 data を JSON 文字列化し Blob として download 起動。
 * 副作用: DOM 操作 (a タグ click + URL.createObjectURL/revokeObjectURL)。
 *
 * 失敗ケース:
 *   - SSR / Node 環境では URL / document が存在せず例外 → 呼び出し側が try/catch
 */
export function exportToJsonFile(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * File オブジェクト (input type=file 由来) を読み JSON.parse。
 * 結果は `unknown` で返し、呼び出し側が parseImportedMasters で type guard を通す。
 */
export async function readJsonFile(file: File): Promise<unknown> {
  const text = await file.text();
  // 空ファイルは null として返さず明示エラー (呼出側が catch して toast 表示)
  if (!text.trim()) {
    throw new Error('ファイルが空です');
  }
  return JSON.parse(text) as unknown;
}

/**
 * master items[] を envelope 化。
 * 出力先 (file / clipboard 等) は呼び出し側で決める。
 */
export function serializeMastersForExport<T>(items: T[]): MasterIoEnvelope<T> {
  return {
    exportedAt: new Date().toISOString(),
    schemaVersion: MASTER_IO_SCHEMA_VERSION,
    items,
  };
}

/**
 * import された raw JSON (envelope or bare array) を validator に通して T[] に変換。
 * 失敗 item は errors に集約して skip (部分成功を許容)。
 *
 * 受け入れ形式:
 *   1. envelope `{ schemaVersion, items: [...] }` (推奨)
 *   2. bare array `[...]` (旧 export 等の互換)
 *   3. 単一 object → 1 要素配列に昇格
 */
export function parseImportedMasters<T>(
  raw: unknown,
  validator: (item: unknown) => T | null,
): ParseResult<T> {
  const errors: string[] = [];
  let rawItems: unknown[] = [];

  if (Array.isArray(raw)) {
    rawItems = raw;
  } else if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.items)) {
      rawItems = obj.items;
    } else {
      // 単一 master object として扱う (1 件 export の互換)
      rawItems = [raw];
    }
  } else {
    return {
      items: [],
      errors: ['JSON の形式が不正です (object または array が必要)'],
    };
  }

  // Sprint BG-MasterIO follow-up: 件数上限チェック (悪意の数千件ファイル防御)
  if (rawItems.length > MAX_IMPORT_ITEMS) {
    return {
      items: [],
      errors: [`件数上限 (${MAX_IMPORT_ITEMS} 件) を超えています: ${rawItems.length} 件`],
    };
  }

  const items: T[] = [];
  rawItems.forEach((rawItem, index) => {
    try {
      const validated = validator(rawItem);
      if (validated === null) {
        errors.push(`Item #${index + 1}: validator が拒否しました`);
        return;
      }
      items.push(validated);
    } catch (e) {
      errors.push(`Item #${index + 1}: ${(e as Error).message}`);
    }
  });

  return { items, errors };
}

/**
 * filename helper: `{collection}-export-YYYY-MM-DD.json` 形式。
 * 個別 row export は `{collection}-{id}-YYYY-MM-DD.json`。
 */
export function buildExportFilename(collectionName: string, rowId?: string): string {
  const date = new Date().toISOString().slice(0, 10);
  if (rowId) return `${collectionName}-${rowId}-${date}.json`;
  return `${collectionName}-export-${date}.json`;
}
